import { api, queryKeys, type Ticket } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, statusTone } from '../../lib/format';
import { Chip } from '../../ui';

const TABS = [
  { label: 'All', to: '/tickets' },
  { label: 'Open', to: '/tickets?status=open' },
  { label: 'Answered', to: '/tickets?status=answered' },
  { label: 'Closed', to: '/tickets?status=closed' },
];

const col = createColumnHelper<Ticket>();

export function TicketList() {
  const { query, setPage } = useTableQuery();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.tickets(query),
    queryFn: () => api.support.adminList(query),
  });

  const columns = useMemo(
    () => [
      col.accessor('id', {
        header: 'Ticket',
        cell: (i) => <Link to={`/tickets/${i.getValue()}`}>#{i.getValue()}</Link>,
      }),
      col.accessor('user_code', { header: 'User ID', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('subject', { header: 'Subject' }),
      col.accessor('category', { header: 'Category', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('priority', { header: 'Priority' }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('updated_at', { header: 'Updated', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Support Tickets" tabs={TABS} />
      <DataTable<Ticket>
        title="Tickets"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
